import React, { useEffect, useState } from "react";
import {
    CreateReviewModal,
    EditReviewModal,
    ModalDelete,
    Pagination,
    RenderEmptyList,
    TooltipCustom,
    TooltipPopover,
    WithRoleAccess,
} from "@services/ui-components";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import TooltipTextForName from "./TooltipTextForName";
import TooltipTextForType from "./TooltipTextForType";
import LabelType from "./LabelType";
import EmployeeInfo from "@services/ui-components/src/EmployeeInfo/EmployeeInfo";
import StarRating from "@services/ui-components/src/StarRating/StarRating";
import { formatDateTime } from "@me-team/host/src/utils/utils";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { Filter, getInitialValueFilter } from "./constants";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import RenderClientName from "./RenderClientName";
import { useParams } from "react-router-dom";
import { useDeleteReviewsMutation, useGetReviewsQuery } from "@me-team/host/main/reviews/graphql/reviews.hooks";
import { CompanyAccess, PermissionScope, ReviewProxy } from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar, currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../hooks/useScrollToTopOnChange";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface ReviewsTable {
    filter: Filter;
    showCreateModal: boolean;
    setShowCreateModal: (showCreateModal: boolean) => void;
    canCreateReview: boolean;
    canCreateReviewAll: boolean;
    canViewReviewsAll: boolean;
    canViewReviewsSelf: boolean;
}

const ReviewsTable: React.FC<ReviewsTable> = ({
    filter,
    showCreateModal,
    setShowCreateModal,
    canCreateReview,
    canCreateReviewAll,
    canViewReviewsAll,
    canViewReviewsSelf,
}) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { userId, branchId } = useParams();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canEditReviewAll = useRoleAccess(CompanyAccess.ReviewEdit, { scope: PermissionScope.All });
    const canEditReviewSelf = useRoleAccess(CompanyAccess.ReviewEdit, { scope: PermissionScope.Self });
    const canDeleteReviewAll = useRoleAccess(CompanyAccess.ReviewDelete, { scope: PermissionScope.All });
    const canDeleteReviewSelf = useRoleAccess(CompanyAccess.ReviewDelete, { scope: PermissionScope.Self });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const initialValueFilter = branchId && getInitialValueFilter(+branchId);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [nameIdDelete, setNameIdDelete] = useState({ name: "", id: null });
    const [idReview, setIdReview] = useState(null);
    const [selectedReviewScope, setSelectedReviewScope] = useState<PermissionScope | null>(null);
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const hasRowActions = canEditReviewAll || canEditReviewSelf || canDeleteReviewAll || canDeleteReviewSelf;
    const shouldShowOnlySelfReviews = !canViewReviewsAll && canViewReviewsSelf;
    const canEditSelectedReview =
        selectedReviewScope === PermissionScope.Self ? canEditReviewSelf || canEditReviewAll : canEditReviewAll;
    const canDeleteSelectedReview =
        selectedReviewScope === PermissionScope.Self ? canDeleteReviewSelf || canDeleteReviewAll : canDeleteReviewAll;

    useScrollToTopOnChange(currentPage);

    const resetCurrentPage = () => {
        setCurrentPage(1);
    };
    useEffect(() => {
        resetCurrentPage();
    }, [
        filter?.employeeId,
        filter?.reviewType,
        filter?.rating,
        filter?.reviewSortField,
        filter?.order,
        filter?.branchId,
    ]);
    const {
        data: reviewsData,
        loading,
        refetch,
    } = useGetReviewsQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            employeeId: shouldShowOnlySelfReviews ? Number(currentBranchEmployeeId) : +userId || filter?.employeeId,
            employeeEmail: null,
            reviewSortField: filter?.reviewSortField,
            order: filter?.order,
            reviewType: filter?.reviewType,
            rating: filter?.rating,
            companyId: +currentCompanyId,
            branchId: filter.branchId !== -1 ? filter.branchId : null,
        },
        skip: !currentCompanyId || (shouldShowOnlySelfReviews && !currentBranchEmployeeId),
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const [deleteReview] = useDeleteReviewsMutation();

    const handleModalDeleteClose = () => setShowModalDelete(false);

    const handleEditReview = (id: number, rowScope: PermissionScope) => {
        const canEditReview =
            rowScope === PermissionScope.Self ? canEditReviewSelf || canEditReviewAll : canEditReviewAll;
        if (!canEditReview) return;

        setIdReview(id);
        setSelectedReviewScope(rowScope);
        setShowCreateEditModal(true);
        refetch();
    };

    const handleShowModalDelete = async (id: number, name: string, rowScope: PermissionScope) => {
        const canDeleteReview =
            rowScope === PermissionScope.Self ? canDeleteReviewSelf || canDeleteReviewAll : canDeleteReviewAll;
        if (!canDeleteReview) return;

        setNameIdDelete({ name: name, id: id });
        setSelectedReviewScope(rowScope);
        setShowModalDelete(true);
    };

    const handleDelete = async () => {
        if (!canDeleteSelectedReview) return;

        await deleteReview({
            variables: {
                id: nameIdDelete?.id,
                companyId: +currentCompanyId,
                branchId: +branchId,
                employeeId: Number(currentBranchEmployeeId),
            },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data && refetch();
                setShowModalDelete(false);
                toggleShowToast(t("Deleted"));
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    };
    const reviews = reviewsData?.user?.company?.reviews?.reviews;
    const timeZone = reviewsData?.user?.company?.timeZone?.name;
    const pageCount = parseInt(reviewsData?.user?.company?.reviews?.pageCount || "0");
    const totalItems = reviewsData?.user?.company?.reviews?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const isFilterNotEmpty =
        initialValueFilter &&
        !Object.keys(initialValueFilter).every(
            (key) => filter[key as keyof Filter] === initialValueFilter[key as keyof Filter]
        );

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    if (loading) return <Spinner />;

    return (
        <>
            <div className="col-12 table-responsive scrollbar position-relative">
                {!!reviews?.length ? (
                    <table className="table table-borderless">
                        <thead>
                            <tr className="fs-7">
                                <th className="py-3 col-2 ps-4 pe-2 align-middle">
                                    {t("Review modal and table.Employee")}
                                </th>
                                <th className="py-3 px-2 col-1 align-middle">{t("Date and Time")}</th>
                                <th className="py-3 px-2 col-1 align-middle">{t("Review modal and table.Rating")}</th>
                                <th className="py-3 px-2 col-1 align-middle">
                                    <div className="d-flex align-items-center">
                                        <span>{t("Client name")}</span>
                                        <TooltipPopover placement="bottom" text={<TooltipTextForName />}>
                                            <i
                                                className="bi bi-info-circle text-dark ms-2"
                                                style={{ fontSize: "16px" }}
                                            ></i>
                                        </TooltipPopover>
                                    </div>
                                </th>
                                <th className="py-3 px-2 col-3 align-middle">{t("Review")}</th>
                                <th className="py-3 px-2 col-1 align-middle text-start">
                                    <span>{t("Type")}</span>
                                    <TooltipPopover placement="bottom" text={<TooltipTextForType />}>
                                        <i
                                            className="bi bi-info-circle text-dark ms-2"
                                            style={{ fontSize: "16px" }}
                                        ></i>
                                    </TooltipPopover>
                                </th>
                                {hasRowActions ? <th className="py-3 px-2 col-1 align-middle"></th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.map((review: ReviewProxy) => {
                                const rowScope =
                                    currentBranchEmployeeId === review?.employee?.id
                                        ? PermissionScope.Self
                                        : PermissionScope.All;
                                const canEditReview =
                                    rowScope === PermissionScope.Self
                                        ? canEditReviewSelf || canEditReviewAll
                                        : canEditReviewAll;
                                const canDeleteReview =
                                    rowScope === PermissionScope.Self
                                        ? canDeleteReviewSelf || canDeleteReviewAll
                                        : canDeleteReviewAll;
                                return (
                                    <tr key={review?.id} className="border-bottom">
                                        <td className="align-middle ps-4 py-4 pe-2">
                                            <WithRoleAccess
                                                anyPermissions={[
                                                    CompanyAccess.EmployeeView,
                                                    CompanyAccess.EmployeeEdit,
                                                ]}
                                                scope={rowScope}
                                                fallback={
                                                    <EmployeeInfo
                                                        name={review?.employee?.name}
                                                        surname={review?.employee?.surname}
                                                        position={review?.employee?.position?.name}
                                                        image={review?.employee?.image}
                                                        userBranchRelations={review?.employee?.userBranchRelations}
                                                        link={false}
                                                        idEmployee={review?.employee?.id}
                                                    />
                                                }
                                            >
                                                <EmployeeInfo
                                                    name={review?.employee?.name}
                                                    surname={review?.employee?.surname}
                                                    position={review?.employee?.position?.name}
                                                    image={review?.employee?.image}
                                                    userBranchRelations={review?.employee?.userBranchRelations}
                                                    link
                                                    idEmployee={review?.employee?.id}
                                                />
                                            </WithRoleAccess>
                                        </td>
                                        <td className="align-middle py-4 px-2">
                                            {formatDateTime(review?.createdAt, timeZone)}
                                        </td>
                                        <td className="align-middle py-4 px-2">
                                            <StarRating rating={review?.rating} />
                                        </td>
                                        <td className="align-middle py-4 px-2">
                                            <RenderClientName
                                                review={review}
                                                currentCompanyId={currentCompanyId}
                                                branchId={branchId}
                                            />
                                        </td>
                                        <td className="align-middle py-4 px-2">{review?.text ? review?.text : "-"}</td>
                                        <td className="align-middle py-4 px-2">
                                            <LabelType status={review?.type} />
                                        </td>
                                        {hasRowActions ? (
                                            <td className="align-middle ps-2 pe-0 py-4">
                                                <div className="d-flex align-items-center justify-content-end gap-3">
                                                    {canEditReview ? (
                                                        <TooltipCustom placement="bottom" text={t("Edit Review(btn)")}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="btn-square p-2"
                                                                style={{ width: "39px", height: "39px" }}
                                                                onClick={() => handleEditReview(review?.id, rowScope)}
                                                            >
                                                                <i className="bi bi-pencil text-dark"></i>
                                                            </Button>
                                                        </TooltipCustom>
                                                    ) : null}
                                                    {canDeleteReview ? (
                                                        <TooltipCustom
                                                            placement="bottom"
                                                            text={t("Delete Review(btn)")}
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="btn-square p-2"
                                                                style={{ width: "39px", height: "39px" }}
                                                                onClick={() =>
                                                                    handleShowModalDelete(
                                                                        review?.id,
                                                                        review?.name,
                                                                        rowScope
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-trash3 text-dark"></i>
                                                            </Button>
                                                        </TooltipCustom>
                                                    ) : null}
                                                </div>
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <RenderEmptyList
                        isFilterNotEmpty={isFilterNotEmpty}
                        emptyFilterTitle="No data found for the given values"
                        emptyListTitle="The list of reviews is empty"
                        onClickButton={() => (canCreateReview ? setShowCreateModal(true) : undefined)}
                        isEmployeeRole={!canCreateReview}
                        visibilityBtn={canCreateReview}
                        buttonName={
                            <span>
                                <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                {t("Add Review")}
                            </span>
                        }
                    />
                )}
            </div>

            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : null}

            <ModalDelete
                show={showModalDelete}
                handleClose={handleModalDeleteClose}
                title={t("Delete Feedback")}
                body={`${t("Do you really want to delete the review")} ${nameIdDelete?.name}?`}
                buttons={[
                    {
                        text: t("cancel"),
                        onClick: handleModalDeleteClose,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                    },
                    {
                        text: t("delete"),
                        onClick: handleDelete,
                        variant: "primary",
                        className: "w-100",
                    },
                ]}
            />

            <EditReviewModal
                isModalOpened={showCreateEditModal}
                setIsModalOpened={setShowCreateEditModal}
                reviewId={idReview}
                onSuccess={refetch}
                toggleShowToast={toggleShowToast}
                canEditReview={canEditSelectedReview}
            />

            <CreateReviewModal
                isModalOpened={showCreateModal}
                setIsModalOpened={setShowCreateModal}
                onSuccess={refetch}
                toggleShowToast={toggleShowToast}
                canCreateReview={canCreateReview}
                canCreateReviewAll={canCreateReviewAll}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default ReviewsTable;
